import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';
import { GuideScreenshot } from '@/components/guides/GuideScreenshot';

const STATUSES: { label: string; tone: string; blurb: string }[] = [
  {
    label: 'Pending',
    tone: 'bg-muted/40 text-muted-foreground',
    blurb: "Session is scheduled. Candidate hasn't opened their NeuraX app yet.",
  },
  {
    label: 'Live',
    tone: 'bg-neurax-mint/15 text-neurax-mint',
    blurb: "Candidate is on a call right now. The pulse dot animates while it's active.",
  },
  {
    label: 'In progress',
    tone: 'bg-neurax-electric/15 text-neurax-electric',
    blurb: 'Interview started but not yet ended. Sometimes used for long-running assessments.',
  },
  {
    label: 'Completed',
    tone: 'bg-neurax-plasma/15 text-neurax-plasma',
    blurb: 'Session ended cleanly. Transcript + coaching threads are available.',
  },
  {
    label: 'Failed',
    tone: 'bg-destructive/15 text-destructive',
    blurb: 'Session errored (bad audio, license not active, crash). Good candidate for a refund.',
  },
];

export function TrackSessionsSection() {
  return (
    <GuideSection
      id="track-sessions"
      eyebrow="Tracking"
      title="Track sessions and read transcripts"
      description="Everything your team does lands in one of two places: the Interviews tab or the Tests tab. Click any row to see the full story."
    >
      <GuideScreenshot
        src="/guides/interviews-list.png"
        alt="Interviews page with a scheduled session row showing candidate initials, name, role, company, status badge, when, duration, messages, and created date"
        caption="Session list — each row is one interview. A colored dot next to the status badge flags pending (amber), live (mint with a pulse), or completed (plasma)."
      />

      <h3 className="font-display text-base font-semibold text-white">Status badges at a glance</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {STATUSES.map((s) => (
          <div key={s.label} className="glass-card flex items-start gap-3 p-3">
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${s.tone}`}
            >
              {s.label}
            </span>
            <p className="text-xs text-muted-foreground">{s.blurb}</p>
          </div>
        ))}
      </div>

      <GuideSteps>
        <GuideStep number={1} title="Open a session to see the full detail">
          <p>
            Click any row. A dialog opens with the candidate name, position, company, and two tabs:{' '}
            <strong>Transcript</strong> and <strong>Coaching</strong>.
          </p>
          <div className="flex flex-wrap gap-2">
            <GuideAction href="/interviews">Open Interviews</GuideAction>
            <GuideAction href="/tests">Open Tests</GuideAction>
          </div>
        </GuideStep>

        <GuideStep number={2} title="Transcript tab">
          <p>
            Shows the conversation as captured by the desktop app: interviewer lines on one side
            (electric blue), candidate lines on the other (mint green). Each segment has a timestamp
            in <code>mm:ss</code> format.
          </p>
          <p>
            Counts at the top: total segments, interviewer segments, candidate segments. Good for
            quickly gauging whether the candidate talked enough.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Coaching tab">
          <p>
            Shows the real-time coaching threads NeuraX surfaced to the candidate during the
            session. Each conversation is collapsible; click to expand and read the full
            back-and-forth between the candidate and the AI.
          </p>
          <p>
            This is the gold — you can see exactly what kind of hint or nudge the candidate was
            leaning on during a specific question.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Sync timing">
          <p>
            Transcripts sync when the candidate ends the session in the desktop app. Typical delay:
            30 to 60 seconds. If it's been more than five minutes and the tab still says{' '}
            <em>"Transcript will appear here"</em>, sync probably failed. Contact support.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="info" title="What 'track everything' really means">
        <p>Three places together give you complete visibility:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <strong>Overview</strong> — KPIs, activity feed, quota. Start of your day.
          </li>
          <li>
            <strong>Interviews / Tests</strong> — session lists with live status.
          </li>
          <li>
            <strong>Session detail dialog</strong> — transcripts and coaching threads once sessions
            complete.
          </li>
        </ul>
        <p>
          There's no separate "live observer" — the session list is your real-time view. Refresh the
          page if you want a manual poll; it auto-refreshes every 30 seconds.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="Something broke?">
        <p>
          For sessions that failed or ended in under two minutes, you can request a refund. That
          functionality lands in the Billing guide; for now, note the session ID and reach out to
          F1Jobs support.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
